<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useActivitiesStore } from 'dashboard/stores/activities';
import { useAlert } from 'dashboard/composables';

import ActivityItem from './ActivityItem.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

const props = defineProps({
  activitableType: {
    type: String,
    required: true,
  },
  activitableId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const activitiesStore = useActivitiesStore();

const showNoteForm = ref(false);
const noteContent = ref('');
const showCallForm = ref(false);
const callContent = ref('');
const callDuration = ref('');

const timeline = computed(() => activitiesStore.getTimeline);
const pagination = computed(() => activitiesStore.getPagination);
const uiFlags = computed(() => activitiesStore.getUIFlags);

const handleAddNote = async () => {
  if (!noteContent.value.trim()) return;
  
  try {
    await activitiesStore.createNote(props.activitableType, props.activitableId, noteContent.value);
    noteContent.value = '';
    showNoteForm.value = false;
    useAlert(t('ACTIVITIES.NOTE_SUCCESS'));
  } catch (error) {
    useAlert(t('ACTIVITIES.NOTE_ERROR'));
  }
};

const handleLogCall = async () => {
  if (!callContent.value.trim()) return;
  
  try {
    await activitiesStore.logCall(
      props.activitableType, 
      props.activitableId, 
      callContent.value,
      callDuration.value ? parseInt(callDuration.value) : null
    );
    callContent.value = '';
    callDuration.value = '';
    showCallForm.value = false;
    useAlert(t('ACTIVITIES.CALL_SUCCESS'));
  } catch (error) {
    useAlert(t('ACTIVITIES.CALL_ERROR'));
  }
};

const loadMore = () => {
  if (!pagination.value || pagination.value.current_page >= pagination.value.total_pages) return;
  
  activitiesStore.fetchTimeline(props.activitableType, props.activitableId, {
    page: pagination.value.current_page + 1,
  });
};

const fetchTimeline = () => {
  activitiesStore.fetchTimeline(props.activitableType, props.activitableId);
};

watch(() => [props.activitableType, props.activitableId], fetchTimeline, { immediate: true });
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Header with Actions -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-n-weak">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('ACTIVITIES.TIMELINE') }}
      </h3>
      <div class="flex items-center gap-2">
        <button
          class="flex items-center gap-1 text-xs text-n-brand hover:text-n-brand-dark font-medium"
          @click="showNoteForm = true"
        >
          <span class="i-lucide-file-text" />
          {{ t('ACTIVITIES.ADD_NOTE') }}
        </button>
        <button
          class="flex items-center gap-1 text-xs text-n-brand hover:text-n-brand-dark font-medium"
          @click="showCallForm = true"
        >
          <span class="i-lucide-phone" />
          {{ t('ACTIVITIES.LOG_CALL') }}
        </button>
      </div>
    </div>

    <!-- Note Form -->
    <div v-if="showNoteForm" class="p-4 border-b border-n-weak bg-n-alpha-black2">
      <Editor
        v-model="noteContent"
        :placeholder="t('ACTIVITIES.NOTE_PLACEHOLDER')"
        class="mb-3"
      />
      <div class="flex justify-end gap-2">
        <Button
          :label="t('CANCEL')"
          size="sm"
          variant="faded"
          @click="showNoteForm = false"
        />
        <Button
          :label="t('SAVE')"
          size="sm"
          :disabled="!noteContent.trim()"
          @click="handleAddNote"
        />
      </div>
    </div>

    <!-- Call Form -->
    <div v-if="showCallForm" class="p-4 border-b border-n-weak bg-n-alpha-black2">
      <textarea
        v-model="callContent"
        :placeholder="t('ACTIVITIES.CALL_PLACEHOLDER')"
        class="w-full px-3 py-2 text-sm bg-n-solid-1 rounded-lg border border-n-weak focus:border-n-brand focus:outline-none mb-2 resize-none"
        rows="3"
      />
      <div class="flex items-center gap-2">
        <input
          v-model="callDuration"
          type="number"
          :placeholder="t('ACTIVITIES.DURATION_MINUTES')"
          class="w-32 px-3 py-1.5 text-sm bg-n-solid-1 rounded-lg border border-n-weak focus:border-n-brand focus:outline-none"
        />
        <div class="flex-1" />
        <Button
          :label="t('CANCEL')"
          size="sm"
          variant="faded"
          @click="showCallForm = false"
        />
        <Button
          :label="t('SAVE')"
          size="sm"
          :disabled="!callContent.trim()"
          @click="handleLogCall"
        />
      </div>
    </div>

    <!-- Timeline -->
    <div class="flex-1 overflow-y-auto">
      <div v-if="uiFlags.fetchingTimeline" class="flex items-center justify-center py-8">
        <Spinner />
      </div>
      
      <div v-else-if="timeline.length === 0" class="flex flex-col items-center justify-center py-12 text-n-slate-11">
        <span class="i-lucide-activity text-3xl mb-3 opacity-50" />
        <span class="text-sm">{{ t('ACTIVITIES.EMPTY') }}</span>
      </div>
      
      <div v-else class="divide-y divide-n-weak">
        <ActivityItem
          v-for="activity in timeline"
          :key="activity.id"
          :activity="activity"
        />
      </div>

      <!-- Load More -->
      <div
        v-if="pagination && pagination.current_page < pagination.total_pages"
        class="flex justify-center py-4"
      >
        <Button
          :label="t('ACTIVITIES.LOAD_MORE')"
          variant="faded"
          size="sm"
          @click="loadMore"
        />
      </div>
    </div>
  </div>
</template>
